import { forwardRef, HTMLAttributes, ReactNode } from 'react';
import { clsx } from 'clsx';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'success' | 'error' | 'warning' | 'info' | 'neutral' | 'verified' | 'pending' | 'outline' | 'top-rated' | 'secondary' | 'danger' | 'available';
  size?: 'sm' | 'md' | 'lg';
  icon?: ReactNode;
  children: ReactNode;
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      variant = 'default',
      size = 'md',
      icon,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const baseStyles = 'inline-flex items-center gap-1 font-medium rounded-full';

    const variants = {
      default: 'bg-primary-100 text-primary-700',
      success: 'bg-emerald-100 text-emerald-700',
      error: 'bg-red-100 text-red-700',
      warning: 'bg-amber-100 text-amber-700',
      info: 'bg-blue-100 text-blue-700',
      neutral: 'bg-gray-100 text-gray-700',
      verified: 'bg-blue-100 text-blue-700',
      pending: 'bg-amber-100 text-amber-700',
      outline: 'bg-white border border-gray-300 text-gray-700',
      'top-rated': 'bg-amber-100 text-amber-700',
      secondary: 'bg-gray-100 text-gray-600',
      danger: 'bg-red-100 text-red-700',
      available: 'bg-emerald-100 text-emerald-700',
    };

    const sizes = {
      sm: 'px-2 py-0.5 text-[10px]',
      md: 'px-2.5 py-1 text-xs',
      lg: 'px-3 py-1.5 text-sm',
    };

    return (
      <span
        ref={ref}
        className={clsx(
          baseStyles,
          variants[variant],
          sizes[size],
          className
        )}
        {...props}
      >
        {icon && <span className="flex-shrink-0">{icon}</span>}
        <span>{children}</span>
      </span>
    );
  }
);

Badge.displayName = 'Badge';
